/**
 * Cart Upsell Popup
 * Shows a popup when specific products are added to cart, offering a bundle upgrade.
 * Supports multiple popup instances (one per bundle rule).
 */

class CartUpsellPopup extends HTMLElement {
  static instances = [];
  static fetchIntercepted = false;
  static pubSubSubscribed = false;
  static pendingCartDrawerResponse = null;
  static pendingCartDrawer = null;
  static _rechargeWatchInterval = null;
  static _rechargeWatchTimeout = null;
  static _hiddenRechargeEls = [];
  static _suppressStyleEl = null;

  constructor() {
    super();

    this.mode = this.dataset.mode || 'switch';
    this.triggerProductHandle = this.dataset.triggerProductHandle || '';
    this.triggerVariantId = this.dataset.triggerVariantId || '';
    this.bundleVariantId = this.dataset.bundleVariantId || '';
    this.excludeVariantId = this.dataset.excludeVariantId || this.bundleVariantId;
    this.savingsAmount = this.dataset.savingsAmount || '30';

    // Add-on mode: one or more trigger handles, adds a separate discounted product
    this.addonVariantId = this.dataset.addonVariantId || '';
    this.discountCode = this.dataset.discountCode || '';
    this.triggerHandles = (this.dataset.triggerHandles || '')
      .split(',')
      .map((h) => h.trim().toLowerCase())
      .filter(Boolean);

    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleSwitchBundle = this.handleSwitchBundle.bind(this);
    this.handleAddAddon = this.handleAddAddon.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    // Register this instance
    CartUpsellPopup.instances.push(this);

    // Setup event listeners for this popup
    this.setupEventListeners();

    // Setup global cart listeners once
    CartUpsellPopup.subscribeToCartUpdates();
  }

  setupEventListeners() {
    // Close buttons and overlay
    this.querySelectorAll('[data-close]').forEach((button) => {
      button.addEventListener('click', this.close);
    });

    // Switch to bundle button
    const switchButton = this.querySelector('[data-switch-bundle]');
    if (switchButton) {
      switchButton.addEventListener('click', this.handleSwitchBundle);
    }

    // Add-on button (adds a separate discounted product)
    const addonButton = this.querySelector('[data-add-addon]');
    if (addonButton) {
      addonButton.addEventListener('click', this.handleAddAddon);
    }

    // Escape key
    document.addEventListener('keydown', this.handleKeyDown);
  }

  static subscribeToCartUpdates() {
    // Only subscribe once across all instances
    if (CartUpsellPopup.pubSubSubscribed) return;
    CartUpsellPopup.pubSubSubscribed = true;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        CartUpsellPopup.handleCartUpdate(event);
      });
    } else {
      document.addEventListener('cart:updated', (event) => {
        CartUpsellPopup.handleCartUpdate(event.detail);
      });

      CartUpsellPopup.interceptCartAdd();
    }
  }

  static interceptCartAdd() {
    if (CartUpsellPopup.fetchIntercepted) return;
    CartUpsellPopup.fetchIntercepted = true;

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch.apply(window, args);

      const url = args[0];
      if (typeof url === 'string' && url.includes('/cart/add')) {
        try {
          const clonedResponse = response.clone();
          await clonedResponse.json();

          setTimeout(() => {
            CartUpsellPopup.checkAllPopups();
          }, 500);
        } catch (e) {
          // Ignore parsing errors
        }
      }

      return response;
    };
  }

  static async handleCartUpdate(event) {
    // Skip our own event after a bundle switch — let Recharge drawer open normally
    if (event?.source === 'cart-upsell-popup') return;

    // Start suppressing external cart drawers (like Recharge) early,
    // before we know if a popup will show — prevents them from
    // appearing simultaneously with the popup
    const variantId = event?.productVariantId;
    const mightShowPopup = CartUpsellPopup.instances.some(popup => {
      if (popup.triggerVariantId && variantId) {
        return String(popup.triggerVariantId) === String(variantId);
      }
      return true;
    });
    if (mightShowPopup) {
      CartUpsellPopup._startDrawerWatch();
    }

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      CartUpsellPopup.checkAllPopupsWithCart(cart);
    } catch (e) {
      console.error('Error fetching cart:', e);
      CartUpsellPopup._stopDrawerWatch();
    }
  }

  static async checkAllPopups() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      CartUpsellPopup.checkAllPopupsWithCart(cart);
    } catch (e) {
      console.error('Error fetching cart:', e);
    }
  }

  static checkAllPopupsWithCart(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      CartUpsellPopup._stopDrawerWatch();
      CartUpsellPopup.openPendingCartDrawer();
      return;
    }

    // Check each popup instance to see if it should show
    for (const popup of CartUpsellPopup.instances) {
      if (popup.shouldShow(cart)) {
        popup.open();
        return; // Popup shown — external drawers stay suppressed until popup closes
      }
    }

    // No popup needed — release suppressed drawers and open the cart drawer
    CartUpsellPopup._stopDrawerWatch();
    CartUpsellPopup.openPendingCartDrawer();
  }

  static openPendingCartDrawer() {
    if (CartUpsellPopup.pendingCartDrawerResponse && CartUpsellPopup.pendingCartDrawer) {
      const response = CartUpsellPopup.pendingCartDrawerResponse;
      const drawer = CartUpsellPopup.pendingCartDrawer;
      CartUpsellPopup.pendingCartDrawerResponse = null;
      CartUpsellPopup.pendingCartDrawer = null;
      drawer.renderContents(response);
    }
  }

  /**
   * Suppresses the Recharge cart drawer so it doesn't compete with the upsell popup.
   * Recharge renders its drawer inside a shadow DOM hosted by
   * #recharge-storefront-experiences__cart-drawer — we hide that host element.
   * Uses CSS (persistent) + JS polling (backup) as dual strategy.
   */
  static _startDrawerWatch() {
    if (CartUpsellPopup._rechargeWatchInterval) return;
    CartUpsellPopup._hiddenRechargeEls = [];

    // Layer 1: CSS — hides the Recharge shadow DOM host whenever popup is open.
    // body.overflow-hidden is added by open(), removed by close().
    if (!CartUpsellPopup._suppressStyleEl) {
      const style = document.createElement('style');
      style.id = 'cart-upsell-suppress';
      style.textContent = `
        body.overflow-hidden #recharge-storefront-experiences__cart-drawer {
          display: none !important;
          visibility: hidden !important;
        }
      `;
      document.head.appendChild(style);
      CartUpsellPopup._suppressStyleEl = style;
    }

    // Layer 2: JS polling — hides the host element directly via inline style.
    // Catches the element even before body.overflow-hidden is applied (early suppression).
    const hideDrawer = () => {
      const host = document.getElementById('recharge-storefront-experiences__cart-drawer');
      if (host && !host.hasAttribute('data-upsell-suppressed')) {
        host.setAttribute('data-upsell-suppressed', '');
        host.style.setProperty('display', 'none', 'important');
        CartUpsellPopup._hiddenRechargeEls.push(host);
      }
    };

    hideDrawer();
    CartUpsellPopup._rechargeWatchInterval = setInterval(hideDrawer, 50);
    CartUpsellPopup._rechargeWatchTimeout = setTimeout(() => {
      CartUpsellPopup._clearWatchTimers();
    }, 8000);
  }

  /**
   * Stops polling and un-hides the Recharge drawer host.
   * CSS layer also deactivates when close() removes body.overflow-hidden.
   */
  static _stopDrawerWatch() {
    CartUpsellPopup._clearWatchTimers();
    CartUpsellPopup._hiddenRechargeEls.forEach(el => {
      el.style.removeProperty('display');
      el.removeAttribute('data-upsell-suppressed');
    });
    CartUpsellPopup._hiddenRechargeEls = [];
  }

  static _clearWatchTimers() {
    if (CartUpsellPopup._rechargeWatchInterval) {
      clearInterval(CartUpsellPopup._rechargeWatchInterval);
      CartUpsellPopup._rechargeWatchInterval = null;
    }
    if (CartUpsellPopup._rechargeWatchTimeout) {
      clearTimeout(CartUpsellPopup._rechargeWatchTimeout);
      CartUpsellPopup._rechargeWatchTimeout = null;
    }
  }

  shouldShow(cart) {
    // Add-on mode: show when any trigger handle is in the cart and the
    // add-on product isn't already there.
    if (this.mode === 'addon') {
      const hasTrigger = cart.items.some((item) => {
        const handle = (item.handle || item.product_handle || '').toLowerCase();
        return this.triggerHandles.some((h) => handle.includes(h));
      });
      if (!hasTrigger) return false;

      const hasAddon = cart.items.some(
        (item) => String(item.variant_id || item.id) === String(this.addonVariantId)
      );
      if (hasAddon) return false;

      return true;
    }

    // Check if the trigger variant is in the cart
    const hasTriggerVariant = cart.items.some((item) => {
      if (this.triggerVariantId) {
        return String(item.variant_id || item.id) === String(this.triggerVariantId);
      }
      const handle = item.handle || item.product_handle || '';
      return handle.toLowerCase().includes(this.triggerProductHandle.toLowerCase());
    });

    if (!hasTriggerVariant) return false;

    // Check if cart already contains the bundle variant
    const hasExcludedVariant = cart.items.some((item) => {
      return String(item.variant_id || item.id) === String(this.excludeVariantId);
    });

    if (hasExcludedVariant) return false;

    return true;
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    if (typeof trapFocus === 'function') {
      trapFocus(this);
    }

    const focusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) {
      focusable.focus();
    }

    this.triggerItem = document.querySelector('.cart-notification-product') ||
      document.querySelector('.product-form__submit') ||
      document.activeElement;
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.triggerItem);
    }

    // Release any suppressed external drawers (like Recharge)
    CartUpsellPopup._stopDrawerWatch();

    // After popup is dismissed, open the pending Dawn cart drawer
    CartUpsellPopup.openPendingCartDrawer();
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      this.close();
    }
  }

  async handleAddAddon() {
    const addonButton = this.querySelector('[data-add-addon]');
    const originalText = addonButton.textContent;

    addonButton.textContent = 'Adding...';
    addonButton.disabled = true;

    try {
      // Apply the discount code FIRST so it is already attached to the cart
      // when the add below fires. Recharge doesn't display Shopify automatic
      // discounts, but it does reflect an applied discount code, and the code
      // activates the moment the add-on (the "get" item) enters the cart, so
      // the drawer that opens on add renders already-discounted.
      // The /discount/CODE endpoint sets the code on the cart session; we
      // ignore its redirect response.
      if (this.discountCode) {
        try {
          await fetch('/discount/' + encodeURIComponent(this.discountCode), {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
        } catch (e) {
          console.error('Error applying discount code:', e);
        }
      }

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.addonVariantId,
          quantity: 1,
        }),
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add add-on to cart');
      }

      // Clear pending cart drawer
      CartUpsellPopup.pendingCartDrawerResponse = null;
      CartUpsellPopup.pendingCartDrawer = null;

      // Close popup — un-hides the Recharge drawer host element, which has
      // already detected the /cart/add.js call and will render the updated cart.
      this.close();

      // Publish with our own source so handleCartUpdate skips it and no
      // further popup (e.g. the grip-meter upgrade) chains off this add.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell-popup' });
      }
    } catch (error) {
      console.error('Error adding add-on:', error);
      addonButton.textContent = 'Error - Try Again';
      setTimeout(() => {
        addonButton.textContent = originalText;
        addonButton.disabled = false;
      }, 2000);
    }
  }

  async handleSwitchBundle() {
    const switchButton = this.querySelector('[data-switch-bundle]');
    const originalText = switchButton.textContent;

    switchButton.textContent = 'Updating cart...';
    switchButton.disabled = true;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const triggerItem = cart.items.find((item) => {
        if (this.triggerVariantId) {
          return String(item.variant_id || item.id) === String(this.triggerVariantId);
        }
        const handle = item.handle || item.product_handle || '';
        return handle.toLowerCase().includes(this.triggerProductHandle.toLowerCase());
      });

      if (!triggerItem) {
        throw new Error('Trigger product not found in cart');
      }

      // Remove the trigger product
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: triggerItem.key,
          quantity: 0,
        }),
      });

      // Add the bundle
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.bundleVariantId,
          quantity: 1,
        }),
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add bundle to cart');
      }

      // Clear pending cart drawer
      CartUpsellPopup.pendingCartDrawerResponse = null;
      CartUpsellPopup.pendingCartDrawer = null;

      // Close popup — this un-hides the Recharge drawer host element.
      // Recharge already detected the /cart/add.js call and will show
      // its drawer with the updated cart contents.
      this.close();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell-popup' });
      }
    } catch (error) {
      console.error('Error switching to bundle:', error);
      switchButton.textContent = 'Error - Try Again';
      setTimeout(() => {
        switchButton.textContent = originalText;
        switchButton.disabled = false;
      }, 2000);
    }
  }
}

if (!customElements.get('cart-upsell-popup')) {
  customElements.define('cart-upsell-popup', CartUpsellPopup);
}
